import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isMobileBrowser } from '../../../base/environment/utils';
import { IconFaceSmile, IconSend } from '../../../base/icons/svg';
import { openChat, sendMessage } from '../../../chat/actions.web';
import SmileysPanel from '../../../chat/components/web/SmileysPanel';
import { CHAR_LIMIT, MESSAGE_TYPE_ERROR } from '../../../chat/constants';
import { areSmileysDisabled, isChatDisabled, isSendGroupChatDisabled } from '../../../chat/functions';
import { QUICK_CHAT_PREVIEW_FADE_OUT_MS, QUICK_CHAT_PREVIEW_MAX_COUNT, QUICK_CHAT_PREVIEW_TEXT_MAX_LENGTH, QUICK_CHAT_PREVIEW_TTL_MS } from '../../../meeting-ui/constants';
import { setToolbarHovered, setToolboxVisible } from '../../actions.web';
import { shouldShowToolbarQuickInput } from '../../functions.web';
/**
 * 截断聊天小窗预览正文。
 *
 * @param {string} text - 原始正文。
 * @returns {string}
 */
function truncatePreviewText(text) {
    if (text.length <= QUICK_CHAT_PREVIEW_TEXT_MAX_LENGTH) {
        return text;
    }
    return `${text.slice(0, QUICK_CHAT_PREVIEW_TEXT_MAX_LENGTH)}…`;
}
/**
 * 判断消息是否仍在预览有效期内。
 *
 * @param {IMessage} message - 聊天消息。
 * @param {number} nowMs - 当前时间戳（毫秒）。
 * @returns {boolean}
 */
function isPreviewMessageActive(message, nowMs) {
    if (typeof message.timestamp !== 'number') {
        return false;
    }
    return nowMs - message.timestamp <= QUICK_CHAT_PREVIEW_TTL_MS;
}
/**
 * 筛选适合在聊天小窗展示的公开消息（最多 N 条，且未过期）。
 *
 * @param {IMessage[]} messages - 全部聊天消息。
 * @param {number} nowMs - 当前时间戳（毫秒）。
 * @returns {IMessage[]}
 */
function getQuickChatPreviewMessages(messages, nowMs) {
    return messages
        .filter(message => !message.privateMessage
        && !message.isReaction
        && !message.lobbyChat
        && message.messageType !== MESSAGE_TYPE_ERROR
        && Boolean(message.message?.trim())
        && isPreviewMessageActive(message, nowMs))
        .slice(-QUICK_CHAT_PREVIEW_MAX_COUNT);
}
/**
 * 将活跃预览与上一帧渲染列表合并：离开活跃集的气泡标记为 fading。
 *
 * @param {IPreviewRenderItem[]} previous - 上一帧渲染项。
 * @param {IMessage[]} activeMessages - 仍在 TTL 内的消息。
 * @returns {IPreviewRenderItem[]}
 */
function mergePreviewRenderItems(previous, activeMessages) {
    const activeById = new Map(activeMessages.map(message => [message.messageId, message]));
    const next = [];
    const seenIds = new Set();
    for (const item of previous) {
        const messageId = item.message.messageId;
        const activeMessage = activeById.get(messageId);
        if (activeMessage) {
            next.push({
                fading: false,
                message: activeMessage
            });
            seenIds.add(messageId);
        }
        else {
            next.push({
                fading: true,
                message: item.message
            });
            seenIds.add(messageId);
        }
    }
    for (const message of activeMessages) {
        if (!seenIds.has(message.messageId)) {
            next.push({
                fading: false,
                message
            });
        }
    }
    return next;
}
/**
 * 底栏左侧聊天小窗：最近消息预览 + 可直接发送的快捷输入。
 *
 * @returns {ReactElement|null}
 */
const ToolbarQuickInput = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const inputRef = useRef(null);
    /** 淡出结束后移除 DOM 的定时器（按 messageId）。 */
    const fadeOutTimersRef = useRef(new Map());
    const chatDisabled = useSelector(isChatDisabled);
    const sendGroupChatDisabled = useSelector(isSendGroupChatDisabled);
    const smileysDisabled = useSelector(areSmileysDisabled);
    const isNarrowLayout = useSelector((state) => state['features/base/responsive-ui'].isNarrowLayout);
    const videoSpaceWidth = useSelector((state) => state['features/base/responsive-ui'].videoSpaceWidth);
    const mainToolbarButtonsThresholds = useSelector((state) => state['features/toolbox'].mainToolbarButtonsThresholds);
    const messages = useSelector((state) => state['features/chat'].messages);
    const [draft, setDraft] = useState('');
    const [showSmileysPanel, setShowSmileysPanel] = useState(false);
    /** 仅用于触发到期后的重渲染；过滤一律用 Date.now()。 */
    const [previewTick, setPreviewTick] = useState(0);
    /** 含淡出中的气泡，驱动实际渲染。 */
    const [renderedPreviews, setRenderedPreviews] = useState([]);
    const activePreviewMessages = useMemo(() => {
        // previewTick 仅用于 TTL 到期后强制重算
        void previewTick;
        return getQuickChatPreviewMessages(messages, Date.now());
    }, [messages, previewTick]);
    // 同步活跃预览 → 渲染列表；离开活跃集的气泡先淡出再移除。
    useEffect(() => {
        setRenderedPreviews(prev => mergePreviewRenderItems(prev, activePreviewMessages));
    }, [activePreviewMessages]);
    // 为 fading 气泡调度淡出结束后卸载；恢复活跃则取消定时器。
    useEffect(() => {
        const fadingIds = new Set(renderedPreviews.filter(item => item.fading).map(item => item.message.messageId));
        const activeIds = new Set(renderedPreviews.filter(item => !item.fading).map(item => item.message.messageId));
        for (const messageId of activeIds) {
            const pendingTimer = fadeOutTimersRef.current.get(messageId);
            if (typeof pendingTimer === 'number') {
                window.clearTimeout(pendingTimer);
                fadeOutTimersRef.current.delete(messageId);
            }
        }
        for (const messageId of fadingIds) {
            if (fadeOutTimersRef.current.has(messageId)) {
                continue;
            }
            const timerId = window.setTimeout(() => {
                fadeOutTimersRef.current.delete(messageId);
                setRenderedPreviews(current => current.filter(entry => entry.message.messageId !== messageId));
            }, QUICK_CHAT_PREVIEW_FADE_OUT_MS);
            fadeOutTimersRef.current.set(messageId, timerId);
        }
    }, [renderedPreviews]);
    // 按最早一条仍活跃预览的到期时间调度刷新。
    useEffect(() => {
        if (activePreviewMessages.length === 0) {
            return;
        }
        const earliestExpiry = Math.min(...activePreviewMessages.map(message => message.timestamp + QUICK_CHAT_PREVIEW_TTL_MS));
        const delayMs = Math.max(0, earliestExpiry - Date.now());
        const timerId = window.setTimeout(() => {
            setPreviewTick(tick => tick + 1);
        }, delayMs);
        return () => {
            window.clearTimeout(timerId);
        };
    }, [activePreviewMessages]);
    // 卸载时清理淡出定时器，避免泄漏。
    useEffect(() => () => {
        for (const timerId of fadeOutTimersRef.current.values()) {
            window.clearTimeout(timerId);
        }
        fadeOutTimersRef.current.clear();
    }, []);
    const isMobile = isMobileBrowser();
    const inputDisabled = sendGroupChatDisabled;
    const canSend = !inputDisabled && Boolean(draft.trim());
    const keepToolboxVisible = useCallback(() => {
        dispatch(setToolboxVisible(true));
        dispatch(setToolbarHovered(true));
    }, [dispatch]);
    const handleOpenChat = useCallback(() => {
        dispatch(openChat());
    }, [dispatch]);
    const handleSend = useCallback(() => {
        if (inputDisabled) {
            return;
        }
        const trimmed = draft.trim();
        if (!trimmed) {
            return;
        }
        dispatch(sendMessage(trimmed));
        setDraft('');
        setShowSmileysPanel(false);
        setPreviewTick(tick => tick + 1);
        inputRef.current?.focus();
        keepToolboxVisible();
    }, [dispatch, draft, inputDisabled, keepToolboxVisible]);
    const handleInputChange = useCallback((event) => {
        const nextValue = event.target.value;
        if (nextValue.length <= CHAR_LIMIT) {
            setDraft(nextValue);
        }
    }, []);
    const handleKeyDown = useCallback((event) => {
        // 输入法合成中不拦截回车，避免中文等输入被提前提交。
        if (event.nativeEvent.isComposing || event.keyCode === 229) {
            return;
        }
        if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey) {
            event.preventDefault();
            event.stopPropagation();
            handleSend();
        }
        if (event.key === 'Escape' && showSmileysPanel) {
            event.preventDefault();
            setShowSmileysPanel(false);
        }
    }, [handleSend, showSmileysPanel]);
    const handleFocus = useCallback(() => {
        keepToolboxVisible();
    }, [keepToolboxVisible]);
    const handleToggleSmileys = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        if (inputDisabled || smileysDisabled) {
            return;
        }
        setShowSmileysPanel(prev => !prev);
        keepToolboxVisible();
        inputRef.current?.focus();
    }, [inputDisabled, keepToolboxVisible, smileysDisabled]);
    const handleSmileySelect = useCallback((smileyText) => {
        if (smileyText) {
            setDraft(prev => {
                const next = `${prev}${prev ? ' ' : ''}${smileyText}`;
                return next.length <= CHAR_LIMIT ? next : prev;
            });
            setShowSmileysPanel(false);
            inputRef.current?.focus();
            keepToolboxVisible();
            return;
        }
        setShowSmileysPanel(false);
    }, [keepToolboxVisible]);
    if (chatDisabled
        || !shouldShowToolbarQuickInput(videoSpaceWidth, isNarrowLayout || isMobile, mainToolbarButtonsThresholds)) {
        return null;
    }
    return (React.createElement("div", { className: 'toolbar-quick-input', onFocus: handleFocus },
        !showSmileysPanel && renderedPreviews.length > 0 && (React.createElement("div", { className: 'toolbar-quick-input-previews' }, renderedPreviews.map(({ fading, message }) => (React.createElement("button", { "aria-hidden": fading, "aria-label": t('toolbar.quickChatPreviewAria', {
                name: message.displayName,
                message: message.message
            }), className: fading
                ? 'toolbar-quick-input-preview toolbar-quick-input-preview-fading'
                : 'toolbar-quick-input-preview', disabled: fading, key: message.messageId, onClick: handleOpenChat, tabIndex: fading ? -1 : 0, type: 'button' },
            React.createElement("span", { className: 'toolbar-quick-input-preview-name' }, message.displayName),
            React.createElement("span", null, truncatePreviewText(message.message))))))),
        showSmileysPanel && !smileysDisabled && (React.createElement("div", { className: 'toolbar-quick-input-smileys' },
            React.createElement(SmileysPanel, { onSmileySelect: handleSmileySelect }))),
        React.createElement("div", { className: `toolbar-quick-input-bar${inputDisabled ? ' toolbar-quick-input-bar-disabled' : ''}` },
            !smileysDisabled && (React.createElement("button", { "aria-label": t('toolbar.quickChatSmileysAria'), className: 'toolbar-quick-input-icon-btn', disabled: inputDisabled, onClick: handleToggleSmileys, type: 'button' },
                React.createElement(IconFaceSmile, null))),
            React.createElement("input", { "aria-label": t('toolbar.quickChatInputAria'), className: 'toolbar-quick-input-field', disabled: inputDisabled, maxLength: CHAR_LIMIT, onChange: handleInputChange, onFocus: handleFocus, onKeyDown: handleKeyDown, placeholder: inputDisabled
                    ? t('chat.disabled')
                    : t('toolbar.quickInputPlaceholder'), ref: inputRef, type: 'text', value: draft }),
            React.createElement("button", { "aria-label": t('toolbar.quickChatSendAria'), className: 'toolbar-quick-input-icon-btn', disabled: !canSend, onClick: handleSend, type: 'button' },
                React.createElement(IconSend, null)))));
};
export default ToolbarQuickInput;
